import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, Video, Users, BookOpen, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface Event {
  id: string;
  title: string;
  description: string | null;
  event_date: string;
  event_type: string;
  location: string | null;
  max_attendees: number | null;
  created_by: string;
  registrations?: { user_id: string }[];
}

const eventIcons = {
  live_session: Video,
  office_hours: Users,
  webinar: Calendar,
  workshop: BookOpen
};

export const EventsCalendar = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    event_date: "",
    event_type: "webinar",
    location: "",
    max_attendees: ""
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    const { data: eventsData } = await supabase
      .from("events")
      .select("*, registrations:event_registrations(user_id)")
      .gte("event_date", new Date().toISOString())
      .order("event_date");

    setEvents(eventsData || []);
    setLoading(false);
  };

  const createEvent = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { error } = await supabase
      .from("events")
      .insert({
        ...newEvent,
        max_attendees: newEvent.max_attendees ? parseInt(newEvent.max_attendees) : null,
        created_by: session.user.id
      });

    if (error) {
      toast.error("Failed to create event");
    } else {
      toast.success("Event created successfully!");
      setDialogOpen(false);
      setNewEvent({
        title: "",
        description: "",
        event_date: "",
        event_type: "webinar",
        location: "",
        max_attendees: ""
      });
      loadEvents();
    }
  };

  const registerForEvent = async (eventId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast.error("Please sign in to register");
      return;
    }

    const { error } = await supabase
      .from("event_registrations")
      .insert({
        event_id: eventId,
        user_id: session.user.id
      });

    if (error) {
      if (error.code === "23505") {
        toast.error("You're already registered for this event");
      } else {
        toast.error("Failed to register");
      }
    } else {
      toast.success("Successfully registered!");
      loadEvents();
      
      // Track activity
      await supabase.from("activity_feed").insert({
        user_id: session.user.id,
        activity_type: "event_registered",
        metadata: { event_id: eventId }
      });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading events...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">Upcoming Events</h2>
          <p className="text-muted-foreground">Join live sessions, webinars, and workshops</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>Create Event</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                placeholder="Event Title"
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
              />
              <Textarea
                placeholder="Description"
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                rows={4}
              />
              <Select
                value={newEvent.event_type}
                onValueChange={(value) => setNewEvent({ ...newEvent, event_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="live_session">Live Session</SelectItem>
                  <SelectItem value="office_hours">Office Hours</SelectItem>
                  <SelectItem value="webinar">Webinar</SelectItem>
                  <SelectItem value="workshop">Workshop</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="datetime-local"
                value={newEvent.event_date}
                onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
              />
              <Input
                placeholder="Location (URL or physical address)"
                value={newEvent.location}
                onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
              />
              <Input
                type="number"
                placeholder="Max Attendees (optional)"
                value={newEvent.max_attendees}
                onChange={(e) => setNewEvent({ ...newEvent, max_attendees: e.target.value })}
              />
              <Button onClick={createEvent} className="w-full">Create Event</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {events.map((event) => {
          const Icon = eventIcons[event.event_type as keyof typeof eventIcons];
          const isRegistered = event.registrations?.some(r => r.user_id);
          const registrationCount = event.registrations?.length || 0;
          const isFull = event.max_attendees && registrationCount >= event.max_attendees;

          return (
            <Card key={event.id} className="hover:border-primary transition-colors">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">{event.title}</CardTitle>
                  </div>
                  {isRegistered && (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                </div>
                <CardDescription>
                  {format(new Date(event.event_date), "PPP 'at' p")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {event.description && (
                  <p className="text-sm text-muted-foreground">{event.description}</p>
                )}
                {event.location && (
                  <p className="text-sm"><strong>Location:</strong> {event.location}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {registrationCount} {event.max_attendees && `/ ${event.max_attendees}`} registered
                  </span>
                  <Button
                    size="sm"
                    onClick={() => registerForEvent(event.id)}
                    disabled={isRegistered || isFull}
                  >
                    {isRegistered ? "Registered" : isFull ? "Full" : "Register"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {events.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No Upcoming Events</h3>
            <p className="text-muted-foreground">Check back soon for new events!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};